// src/pages/informes/components/CartulinaModal.tsx
import React, { useEffect, useRef, useState } from "react";
import {
    Modal, Paper, Box, Typography, IconButton, CircularProgress, Grid
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import moment from "moment-timezone";
import SimpleCustomTable from "../../../components/table/SimpleCustomTable";
import { SimpleColumn } from "../../../components/table/interface";
import EmptyStateMessage from "../../../components/layouts/EmptyStateMessage";
import { styled, useTheme } from "@mui/material/styles";

type CartulinaItem = {
    id: number;
    total_value: number;
    balance: number;
    client: {
        id: number;
        idnumber: string | number;
        fullname: string;
    };
};

export interface CartulinaModalProps {
    open: boolean;
    onClose: () => void;
    collectId: number | null;      // requerido
    date: string | null;           // YYYY-MM-DD
    collectName: string | null;
    showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
    showLoading: () => void;
    hideLoading: () => void;
}

const formatCurrency = (v: number) =>
    new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(Number(v || 0));

const SummaryBox = styled(Paper)(({ theme }) => ({
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[0],
    border: `1px solid ${theme.palette.divider}`,
}));
const CartulinaModal: React.FC<CartulinaModalProps> = ({
    open, onClose, collectId, date, collectName, showAlert, showLoading, hideLoading
}) => {
    const theme = useTheme();
    const [items, setItems] = useState<CartulinaItem[]>([]);
    const [loading, setLoading] = useState(false);

    // anti-doble fetch (React 18 strict mode) + evitar condiciones de carrera
    const didMountRef = useRef(false);
    const reqIdRef = useRef(0);

    const rows = items.map((it, idx) => ({
        id: it.id ?? idx,                                      // DataGrid id
        client_nit: it.client?.idnumber ?? "",
        client_name: it.client?.fullname ?? "",
        loan_value: it.total_value ?? 0,
        balance: it.balance ?? 0,
        loan_date: moment(date ?? "").format("YYYY-MM-DD"),
    }));

    const sumLoan = rows.reduce((a, r) => a + Number(r.loan_value || 0), 0);
    const sumBalance = rows.reduce((a, r) => a + Number(r.balance || 0), 0);

    useEffect(() => {
        if (!open || !collectId || !date) return;

        // Permite refetch al cambiar collectId/date; evita segundo disparo del strict mode en dev
        if (!didMountRef.current) {
            didMountRef.current = true;
        }

        let active = true;
        const myReqId = ++reqIdRef.current;

        const fetchData = async () => {
            setLoading(true);
            showLoading();
            try {
                const payload = {
                    collect_id: [collectId],                      // siempre arreglo
                    date: moment(date).format("YYYY/MM/DD"),      // formato acordado
                };
                const { data } = await axios.post("/inform/summaryclient", payload);
                if (!active || myReqId !== reqIdRef.current) return;
                setItems(Array.isArray(data?.data) ? data.data : []);
            } catch (e) {
                if (active) {
                    console.error(e);
                    showAlert("No fue posible cargar la cartulina.", "error");
                }
            } finally {
                if (active) {
                    setLoading(false);
                    hideLoading();
                }
            }
        };

        fetchData();

        return () => {
            active = false;
        };
    }, [open, collectId, date]); // 👈 solo dependencias necesarias

    const columns: SimpleColumn[] = [
        {
            field: "client_nit",
            headerName: "Cédula",
            minWidth: 80,
            wrapText: true,
        },
        {
            field: "client_name",
            headerName: "Cliente",
            minWidth: 100,
            maxWidth: 150,
            wrapText: true,
        },
        {
            field: "loan_date",
            headerName: "Fecha Préstamo",
            align: "center",
            minWidth: 80,
        },
        {
            field: "loan_value",
            headerName: "Valor Préstamo",
            align: "right",
            minWidth: 80,
            renderCell: ({ row }) => (
                <Typography variant="body2">{formatCurrency(Number(row.loan_value ?? 0))}</Typography>
            ),
        },
        {
            field: "balance",
            headerName: "Saldo",
            align: "right",
            minWidth: 80,
            renderCell: ({ row }) => (
                <Typography variant="body2">{formatCurrency(Number(row.balance ?? 0))}</Typography>
            ),
        },
    ];

    return (
        <Modal open={open} onClose={onClose}>
            <Paper sx={{
                position: "absolute",
                top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                width: "95vw", maxWidth: 900, maxHeight: "85vh",
                display: "flex", flexDirection: "column", borderRadius: 2, overflow: "hidden"
            }}>
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, bgcolor: "primary.main", color: "#fff" }}>
                    <Typography variant="h6">
                        {`Fecha: ${moment(date ?? "").format("YYYY-MM-DD")}`}
                        {collectId ? `  |  Cobro ${collectName}` : ""}
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Body */}
                <Box sx={{ p: 2, pt: 1, display: "flex", flexDirection: "column", gap: 1, overflow: "scroll" }}>
                    <Box sx={{ flex: 1, minHeight: 360 }}>
                        {items.length > 0 ? (
                            <>
                                <SimpleCustomTable
                                    rows={rows}
                                    columns={columns}
                                    maxHeight={360}
                                    showBorders
                                    showCount
                                    exportCsv
                                    exportFileName={`cartulina-${collectId ?? ""}.csv`}
                                    wrapCellContent
                                />
                            </>
                        ) : loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <EmptyStateMessage message="No existen préstamos con saldo para este día." align="center" />
                        )}
                    </Box>

                </Box>
            </Paper>
        </Modal>
    );
};

export default CartulinaModal;
